/**
 * G2eGente - Criar/Editar Ciclo de Avaliação
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { performanceCyclesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, 
  Loader2,
  Save,
  Calendar,
  Settings,
  Eye,
  Percent
} from 'lucide-react';
import { toast } from 'sonner';

const PERIODICIDADES = [
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
  { value: 'custom', label: 'Personalizado' }
];

export default function CycleForm() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    nome: '',
    periodo_inicio: '',
    periodo_fim: '',
    periodicidade: 'semestral',
    permite_autoavaliacao: true,
    permite_avaliacao_pares: false,
    funcionario_ve_nota: true,
    funcionario_ve_comentarios: true,
    funcionario_ve_comparativo: false,
    peso_competencias_tecnicas: 40,
    peso_competencias_comportamentais: 40,
    peso_disc: 20
  });

  const totalPeso = form.peso_competencias_tecnicas + form.peso_competencias_comportamentais + form.peso_disc;
  const pesoValid = totalPeso === 100;

  const handlePesoChange = (field, value) => {
    setForm({ ...form, [field]: value[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.nome || !form.periodo_inicio || !form.periodo_fim) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    if (!pesoValid) {
      toast.error('Os pesos devem somar 100%');
      return;
    }
    
    setSaving(true);
    try {
      await performanceCyclesAPI.create(form);
      toast.success('Ciclo criado com sucesso!');
      navigate('/company/performance');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar ciclo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="cycle-form">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/company/performance')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Novo Ciclo de Avaliação</h1>
            <p className="text-slate-600">Configure um período de avaliações para sua equipe</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações Básicas */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-cyan-600" />
                Informações do Ciclo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Ciclo *</Label>
                <Input
                  id="nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Ex: Avaliação 2026 - 1º Semestre"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="periodo_inicio">Data de Início *</Label>
                  <Input
                    id="periodo_inicio"
                    type="date"
                    value={form.periodo_inicio}
                    onChange={(e) => setForm({ ...form, periodo_inicio: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="periodo_fim">Data de Término *</Label>
                  <Input
                    id="periodo_fim"
                    type="date"
                    value={form.periodo_fim}
                    onChange={(e) => setForm({ ...form, periodo_fim: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Periodicidade</Label>
                <Select 
                  value={form.periodicidade} 
                  onValueChange={(v) => setForm({ ...form, periodicidade: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODICIDADES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Configurações de Avaliação */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-cyan-600" />
                Tipos de Avaliação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Permitir Autoavaliação</Label>
                  <p className="text-sm text-slate-500">Funcionários podem avaliar a si mesmos</p>
                </div>
                <Switch
                  checked={form.permite_autoavaliacao}
                  onCheckedChange={(v) => setForm({ ...form, permite_autoavaliacao: v })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Permitir Avaliação de Pares</Label>
                  <p className="text-sm text-slate-500">Colegas do mesmo nível podem avaliar</p>
                </div>
                <Switch
                  checked={form.permite_avaliacao_pares}
                  onCheckedChange={(v) => setForm({ ...form, permite_avaliacao_pares: v })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Visibilidade */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-cyan-600" />
                Visibilidade para o Funcionário
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Ver Nota Final</Label>
                  <p className="text-sm text-slate-500">Funcionário visualiza sua pontuação</p>
                </div>
                <Switch
                  checked={form.funcionario_ve_nota}
                  onCheckedChange={(v) => setForm({ ...form, funcionario_ve_nota: v })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Ver Comentários</Label>
                  <p className="text-sm text-slate-500">Funcionário visualiza feedback escrito</p>
                </div>
                <Switch
                  checked={form.funcionario_ve_comentarios}
                  onCheckedChange={(v) => setForm({ ...form, funcionario_ve_comentarios: v })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Ver Comparativo</Label>
                  <p className="text-sm text-slate-500">Funcionário vê posição em relação à equipe</p>
                </div>
                <Switch
                  checked={form.funcionario_ve_comparativo}
                  onCheckedChange={(v) => setForm({ ...form, funcionario_ve_comparativo: v })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Pesos */}
          <Card className={`border-2 ${pesoValid ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'}`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-cyan-600" />
                  Pesos dos Componentes
                </span>
                <span className={`text-sm font-normal px-3 py-1 rounded-full ${pesoValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  Total: {totalPeso}%
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Competências Técnicas</Label>
                  <span className="font-bold text-blue-600">{form.peso_competencias_tecnicas}%</span>
                </div>
                <Slider
                  value={[form.peso_competencias_tecnicas]}
                  onValueChange={(v) => handlePesoChange('peso_competencias_tecnicas', v)}
                  max={100}
                  step={5}
                  className="w-full"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Competências Comportamentais</Label>
                  <span className="font-bold text-green-600">{form.peso_competencias_comportamentais}%</span>
                </div>
                <Slider
                  value={[form.peso_competencias_comportamentais]}
                  onValueChange={(v) => handlePesoChange('peso_competencias_comportamentais', v)}
                  max={100}
                  step={5}
                  className="w-full"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Perfil DISC</Label>
                  <span className="font-bold text-purple-600">{form.peso_disc}%</span>
                </div>
                <Slider
                  value={[form.peso_disc]}
                  onValueChange={(v) => handlePesoChange('peso_disc', v)}
                  max={100}
                  step={5}
                  className="w-full"
                />
              </div>

              {!pesoValid && (
                <p className="text-red-600 text-sm">
                  ⚠️ Os pesos devem somar exatamente 100%
                </p>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate('/company/performance')}
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              className="bg-cyan-600 hover:bg-cyan-700"
              disabled={saving || !pesoValid}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Criar Ciclo
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
